import { z } from "zod";
import { APIEnvelope, APIError } from "./types/api.zod";

export type ValidationErrorEntry = {
  field: string;
  message: string;
};

export type ValidationEnvelope = {
  errors: ValidationErrorEntry[];
  message: string;
};

export type QRWizardIssue = {
  path: string[];
  message: string;
};

export type QRWizardParseResult =
  | {
      success: true;
      data: Record<string, unknown>;
      issues: [];
    }
  | {
      success: false;
      error: string;
      issues: QRWizardIssue[];
    };

export type WMUZodAPI = {
  parseValidationEnvelope: (payload: unknown) => ValidationEnvelope;
  safeParseQRWizardContent: (typeKey: string, payload: unknown) => QRWizardParseResult;
};

type ValidationEnvelopeInput = {
  errors?: unknown;
  data?: {
    errors?: unknown;
  } | null;
  error?: unknown;
  message?: unknown;
} | null;

const stringField = z.string();
const booleanField = z.boolean();
const nonNegativeInteger = z.number().int().min(0);
const positiveInteger = z.number().int().positive();
const stringArray = z.array(z.string());
const genericObject = z.record(z.string(), z.unknown());

const factionVariantSchema = z.object({
  title: stringField,
  body: stringField,
  prompt: stringField,
  cta_label: stringField,
  cta_url: stringField,
}).strict();

const questionIdentityMode = z.enum(["anonymous", "nickname", "full_name"]);
const trialTierSchema = z.object({
  name: stringField,
  min: nonNegativeInteger.optional(),
  max: nonNegativeInteger.optional(),
  grant_tokens: nonNegativeInteger.optional(),
  grant_items: stringArray.optional(),
}).strict();

const qrWizardContentSchemas: Record<string, z.ZodType<Record<string, unknown>>> = {
  QUEST: z.object({
    title_override: stringField,
    completion_message: stringField,
  }).strict(),
  REVEAL: z.object({
    title: stringField,
    body: stringField,
    image_url: stringField,
    reveal_style: stringField,
  }).strict(),
  GATE: z.object({
    station_name: stringField,
    pass_message: stringField,
    fail_message: stringField,
    volunteer_notes: stringField,
  }).strict(),
  CHECKIN: z.object({
    message: stringField,
    require_gps: booleanField,
    gps_radius: positiveInteger,
  }).strict(),
  VOTE: z.object({
    title: stringField,
    options: z.array(z.object({
      label: stringField,
      value: stringField,
    }).strict()).min(2, "Add at least 2 vote options."),
    max_picks: positiveInteger,
    show_results: booleanField,
    token_reward: nonNegativeInteger,
  }).strict(),
  CONFESS: z.object({
    title: stringField,
    prompt: stringField,
    ack_message: stringField,
    moderation_note: stringField,
  }).strict(),
  CEREMONY: z.object({
    title: stringField,
    script: stringField,
    min_group: positiveInteger,
    description: stringField,
    mobile_relay_enabled: booleanField,
  }).strict(),
  FACTION_MSG: z.object({
    default_variant: factionVariantSchema,
    group_variants: z.record(z.string(), factionVariantSchema),
    delivery_mode: z.enum(["message_only", "message_and_board"]),
    allow_response: booleanField,
    title: stringField,
    allow_reply: booleanField,
    reply_prompt: stringField,
  }).strict(),
  DISPLAY: z.object({
    channel_name: stringField,
    display_title: stringField,
    data_source: stringField,
    source: stringField,
    qr_code_id: stringField,
    display_mode: stringField,
    show_resonance_count: booleanField,
  }).strict(),
  DISPATCH: z.object({
    dispatch_name: stringField,
    location: stringField,
    timezone: stringField,
    publish_time: stringField,
    render_style: stringField,
    carry_forward_prompt: stringField,
    archive_accessible: booleanField,
    read_receipt_mode: stringField,
  }).strict(),
  BOON: z.object({
    boon_key: stringField,
    status: stringField,
    duration_minutes: positiveInteger,
    stack_policy: z.enum(["replace", "extend", "ignore"]),
    scope: z.enum(["individual", "group"]),
    visible_label: stringField,
    on_apply: z.object({
      grant_tokens: nonNegativeInteger,
    }).strict(),
  }).strict(),
  GUESS: z.object({
    guess_key: stringField,
    prompt: stringField,
    options: stringArray.min(2).max(6),
    show_match_score: booleanField,
    repeat_policy: z.enum(["once_per_pair"]),
  }).strict(),
  BOUNTY: z.object({
    title: stringField,
    description: stringField,
    max_claims: nonNegativeInteger,
    token_reward: nonNegativeInteger,
    item_id: stringField,
    show_claimants: booleanField,
    consolation_token_reward: nonNegativeInteger,
  }).strict(),
  CIRCLE: z.object({
    circle_name: stringField,
    location_hint: stringField,
    prompt: stringField,
    sub_prompt: stringField,
    reveal_style: stringField,
    quorum: nonNegativeInteger,
    allow_multiple_submissions: booleanField,
    max_contribution_chars: z.number().int().min(50),
    contributions_public_after: stringField,
    gm_can_read_before_reveal: booleanField,
  }).strict(),
  FIREFLY: z.object({
    firefly_name: stringField,
    performer_hint: stringField,
    auto_expire_after_scan: booleanField,
    ttl_sec: positiveInteger,
    encounter_library: z.array(genericObject, {
      invalid_type_error: "Encounter library must be a JSON array.",
    }),
  }).strict(),
  WORKSHOP: z.object({
    location_label: stringField,
    max_participants: nonNegativeInteger,
    session_duration_minutes: z.number().int().min(5),
    rating_window_minutes: z.number().int().min(5),
    allow_participant_host: booleanField,
    allow_volunteer_host: booleanField,
    host_token_reward: nonNegativeInteger,
    attend_token_reward: nonNegativeInteger,
    word_cloud_display_qr_id: stringField,
  }).strict(),
  RALLY: z.object({
    rally_key: stringField,
    min_size: z.number().int().min(2).max(100),
    max_size: z.number().int().min(2).max(100),
    uniqueness_window: z.enum(["none", "scene", "day", "event"]),
    follow_up: z.object({
      grant_status: stringField,
      grant_mark: stringField,
    }).strict(),
    requires_mix: z.object({
      factions: nonNegativeInteger.max(10),
    }).strict(),
  }).strict(),
  SEAT: z.object({
    location_name: stringField,
    seat_label: stringField,
    assignment_mode: stringField,
    pairing_note_style: stringField,
    unassigned_message: stringField,
  }).strict(),
  LIVING_BOOK: z.object({
    library_name: stringField,
    location: stringField,
    max_concurrent_loans: positiveInteger,
    loan_duration_minutes: positiveInteger,
    tags: stringArray.min(1),
    registration_prompt: stringField,
    reflection_prompt: stringField,
    allow_anonymous_browse: booleanField,
  }).strict(),
  SHADOW: z.object({
    assignment_opens_at: stringField,
    observation_closes_at: stringField,
    reveal_opens_at: stringField,
    max_observations_per_day: positiveInteger,
    assignment_mode: stringField,
    exclude_same_faction: booleanField,
  }).strict(),
  SKILLS: z.object({
    location_label: stringField,
    max_teach_skills: positiveInteger,
    max_learn_skills: positiveInteger,
    session_duration_minutes: positiveInteger,
    free_blocks: z.array(z.unknown(), {
      invalid_type_error: "Free blocks must be a JSON array.",
    }),
    matching_runs_minutes_before: positiveInteger,
    skill_categories: stringArray.min(1),
    allow_browse: booleanField,
  }).strict(),
  SOCIAL_CAPITAL: z.object({
    starting_tokens: positiveInteger,
    influence_threshold: positiveInteger,
    influence_label: stringField,
    hoarding_reminder_day: positiveInteger,
    allow_view_own_balance: booleanField,
    allow_view_influence_board: booleanField,
    public_giver: booleanField,
  }).strict(),
  ASK: z.object({
    question_key: stringField,
    prompt: stringField,
    options: z.array(stringField).min(2, "Add at least 2 options.").max(5, "Add no more than 5 options."),
    response_mode: z.enum(["tap", "swipe"]),
    repeat_policy: z.enum(["once_per_pair", "always"]),
    allow_skip: booleanField,
  }).strict(),
  MOMENT: z.object({
    location_label: stringField,
    moment_duration_minutes: positiveInteger,
    allow_performer_initiation: booleanField,
    performer_user_ids: stringArray,
    broadcast_on_initiation: booleanField,
    emoji_set: stringArray.min(1),
    max_emoji_submissions: positiveInteger,
  }).strict(),
  QUESTION: z.object({
    location_label: stringField,
    prompt: stringField,
    submission_limit_per_user: positiveInteger,
    allow_responses: booleanField,
    allowed_identity_modes: z.array(questionIdentityMode).min(1),
    default_identity_mode: questionIdentityMode,
    response_anonymous: booleanField,
    moderation_required: booleanField,
    resonance_label: stringField,
    display_order: stringField,
    max_questions_shown: positiveInteger,
  }).strict(),
  MATCH: z.object({
    match_key: stringField,
    prompt: stringField,
    options: z.array(z.object({
      label: stringField,
      value: stringField,
    }).strict()).min(2, "Add at least 2 match options."),
    scoring: z.enum(["exact_match"]),
    repeat_policy: z.enum(["once_per_pair"]),
    offline_profile: z.enum(["offline_queueable", "offline_safe", "online_required"]),
  }).strict(),
  TRIAL: z.object({
    trial_key: stringField,
    location_label: stringField.optional().default(""),
    mode: z.enum(["numeric_score", "time_ms", "tier_only"]),
    best_of: positiveInteger,
    leaderboard_scope: z.enum(["event", "qr"]),
    team_mode: booleanField,
    max_participants: positiveInteger,
    score_label: stringField,
    score_unit: z.string().optional().default(""),
    lower_is_better: booleanField,
    notes_placeholder: z.string().optional().default(""),
    tiers: z.array(trialTierSchema).optional().default([]),
  }).strict(),
  THRESHOLD: z.object({
    threshold_id: stringField,
    phase_name: stringField,
    transition_message: stringField,
    reflection_prompt: stringField,
    collect_reflection: booleanField,
    crossing_label: stringField,
    unlock_secret: stringField,
    render_style: stringField,
    journey_label: stringField,
  }).strict(),
};

const validationErrorSchema = z.object({
  field: z.string().optional().default(""),
  message: z.string().trim().min(1),
}).passthrough();

const validationPayloadSchema = APIEnvelope.extend({
  data: z.object({
    errors: z.array(validationErrorSchema).optional(),
  }).partial().passthrough().optional(),
  error: z.union([
    z.string(),
    APIError.pick({
      message: true,
    }).partial().passthrough(),
  ]).optional(),
  errors: z.array(validationErrorSchema).optional(),
  message: z.string().optional(),
}).passthrough();

function issuePath(issue: z.ZodIssue): string[] {
  return issue.path.map((segment) => String(segment));
}

function safeString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function parseValidationEnvelope(payload: unknown): ValidationEnvelope {
  const parsed = validationPayloadSchema.safeParse(payload as ValidationEnvelopeInput);
  if (!parsed.success) {
    return { errors: [], message: "" };
  }

  const data = parsed.data;
  const errors = Array.isArray(data.errors)
    ? data.errors
    : Array.isArray(data.data?.errors)
      ? data.data.errors
      : [];

  let message = safeString(data.message);
  if (!message) {
    if (typeof data.error === "string") {
      message = safeString(data.error);
    } else if (data.error && typeof data.error === "object") {
      message = safeString((data.error as { message?: string }).message);
    }
  }

  return { errors, message };
}

function formatQRWizardIssues(issues: z.ZodIssue[]): QRWizardIssue[] {
  return issues.map((issue) => ({
    path: issuePath(issue),
    message: issue.message,
  }));
}

function safeParseQRWizardContent(typeKey: string, payload: unknown): QRWizardParseResult {
  const normalizedType = String(typeKey || "").trim().toUpperCase();
  const schema = qrWizardContentSchemas[normalizedType];
  if (!schema) {
    return {
      success: true,
      data: (payload && typeof payload === "object") ? payload as Record<string, unknown> : {},
      issues: [],
    };
  }

  const parsed = schema.safeParse(payload);
  if (parsed.success) {
    return {
      success: true,
      data: parsed.data,
      issues: [],
    };
  }

  const issues = formatQRWizardIssues(parsed.error.issues);
  return {
    success: false,
    error: issues[0]?.message || "Invalid QR content.",
    issues,
  };
}

window.WMUZod = {
  parseValidationEnvelope,
  safeParseQRWizardContent,
} satisfies WMUZodAPI;

export {};
